import { useEffect, useState } from 'react';
import OpenRotorMark from '@/components/base/OpenRotorMark';
import { Link, useLocation } from 'react-router-dom';

const routeLinks = [
  { label: 'Products', href: '/products' },
  { label: 'Supply Services', href: '/services' },
  { label: 'Industries', href: '/industries' },
  { label: 'Support', href: '/support' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const links = routeLinks;
  const renderLink = (l: { label: string; href: string }, cls: string, onClick?: () => void) => (l.href.startsWith('/')
    ? <Link key={l.href} to={l.href} onClick={onClick} className={`${cls} ${pathname.startsWith(l.href) ? '!opacity-100 text-primary-500' : ''}`}>{l.label}</Link>
    : isHome
      ? <a key={l.href} href={l.href} onClick={onClick} className={cls}>{l.label}</a>
      : <Link key={l.href} to={`/${l.href}`} onClick={onClick} className={cls}>{l.label}</Link>);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const light = scrolled || open || !isHome;
  const text = light ? 'text-foreground-950' : 'text-foreground-50';

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${light ? 'bg-background-50 border-b border-background-200' : 'bg-transparent'}`}>
      <nav className={`flex items-center justify-between h-16 md:h-20 px-4 md:px-10 ${text}`}>
        {(() => {
          const brand = (
            <>
              <OpenRotorMark size={34} title="NORTHLINE" className="w-[30px] h-[30px] md:w-[34px] md:h-[34px]" />
              <span className="flex flex-col leading-none">
                <span className="font-heading font-medium tracking-[0.14em] text-sm">NORTHLINE</span>
                <span className="font-label text-[9px] tracking-[0.3em] opacity-70 mt-1">FLOW SYSTEMS</span>
              </span>
            </>
          );
          const cls = 'flex items-center gap-2.5 md:gap-3 cursor-pointer';
          return isHome
            ? <a href="#top" aria-label="NORTHLINE Flow Systems home" className={cls}>{brand}</a>
            : <Link to="/" aria-label="NORTHLINE Flow Systems home" className={cls}>{brand}</Link>;
        })()}
        <ul className="hidden md:flex items-center gap-8 font-label text-xs tracking-[0.08em] uppercase">
          {links.map((l) => (
            <li key={l.href}>
              {renderLink(l, 'whitespace-nowrap cursor-pointer opacity-80 hover:opacity-100 hover:text-primary-500 transition-colors')}
            </li>
          ))}
        </ul>
        <Link to="/request-quote" className={`hidden md:inline-flex items-center gap-2 whitespace-nowrap cursor-pointer bg-transparent border font-label text-xs tracking-[0.08em] uppercase px-5 py-2.5 rounded-sm transition-colors duration-300 ${light ? 'border-foreground-950/70 hover:bg-foreground-950 hover:text-background-50' : 'border-foreground-50/80 hover:bg-foreground-50/10 hover:border-foreground-50'}`}>
          Request a Quote <i className="ri-arrow-right-up-line" />
        </Link>
        <button type="button" aria-label="Toggle menu" aria-expanded={open} onClick={() => setOpen(!open)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer whitespace-nowrap">
          <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
        </button>
      </nav>
      {open && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 pb-6">
          {links.map((l) => renderLink(l, 'block py-4 border-b border-background-200 font-label text-xs tracking-[0.14em] uppercase text-foreground-950 cursor-pointer', () => setOpen(false)))}
          <Link to="/request-quote" onClick={() => setOpen(false)} className="mt-5 block text-center bg-primary-500 text-foreground-50 font-label text-xs tracking-[0.14em] uppercase py-3 rounded-sm cursor-pointer whitespace-nowrap">Request a Quote</Link>
        </div>
      )}
    </header>
  );
}